import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import apiClient from "../api/client";
import { useDistanceUnit } from "../contexts/DistanceUnitContext";
import Sidebar from "./Sidebar";
import ProfilePicture from "./ProfilePicture";
import NotificationBell from "./NotificationBell";
import iconMenu from "../assets/icon-menu.svg";
import "./Dashboard.css";

const FriendProfile = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [friendData, setFriendData] = useState(null);
  const [personalCard, setPersonalCard] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [cardText, setCardText] = useState("");
  const [cardColor, setCardColor] = useState("#ffffff");
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [profileData, setProfileData] = useState(null);
  const { convertDistance, getUnitLabel } = useDistanceUnit();

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const fetchFriendProfile = useCallback(async () => {
    try {
      // Fetch friend's stats, personal card, and current user profile
      const [friendResponse, profileResponse] = await Promise.all([
        apiClient.get(`/friends/profile/${userId}`),
        apiClient.get('/profile').catch((error) => {
          console.error('Error fetching profile:', error);
          // Profile fetch failures shouldn't block friend profile view
          if (error.response?.status === 404) {
            console.warn('Own profile not found - 404');
          }
          return { data: { profile: null } };
        })
      ]);

      setFriendData(friendResponse.data.friend_stats);
      setPersonalCard(friendResponse.data.personal_card);
      setProfileData(profileResponse.data.profile);

      if (friendResponse.data.personal_card) {
        setCardText(friendResponse.data.personal_card.card_text || "");
        setCardColor(friendResponse.data.personal_card.card_color || "#ffffff");
      }

      setLoading(false);
    } catch (error) {
      console.error("Error fetching friend profile:", error);
      if (error.response?.status === 403) {
        alert("You don't have permission to view this profile. You must be friends to view their stats.");
        navigate("/friends");
      } else if (error.response?.status === 404) {
        alert("Friend profile not found. They may have deleted their account.");
        navigate("/friends");
      } else {
        alert("Unable to load friend profile. Please try again.");
        navigate("/friends");
      }
      setLoading(false);
    }
  }, [userId, navigate]);

  useEffect(() => {
    fetchFriendProfile();
  }, [fetchFriendProfile]);

  const savePersonalCard = async () => {
    try {
      await apiClient.post(`/friends/card/${userId}`, {
        card_text: cardText,
        card_color: cardColor
      });

      alert("Personal card saved!");
      setIsEditing(false);
      fetchFriendProfile();
    } catch (error) {
      console.error("Error saving personal card:", error);
      alert("Failed to save personal card");
    }
  };

  const convertMilesToUnit = (miles) => {
    return Math.round(convertDistance(miles * 1609.34));
  };

  if (loading) {
    return (
      <div className="dashboard-new">
        <div className="dashboard-loading">Loading profile...</div>
      </div>
    );
  }

  if (!friendData) {
    return (
      <div className="dashboard-new">
        <Sidebar isOpen={sidebarOpen} onToggle={toggleSidebar} />
        <div className={`dashboard-main-container ${sidebarOpen ? 'sidebar-open' : 'sidebar-closed'}`}>
          <header className="dashboard-header">
            <div className="header-left">
              <button className="icon-button" onClick={toggleSidebar}>
                <img src={iconMenu} alt="Menu" />
              </button>
              <h1 className="header-title">Friend Not Found</h1>
            </div>
          </header>
          <div className="dashboard-content">
            <div className="chart-card">
              <p>This friend's profile is not available or they haven't enabled stats sharing.</p>
              <button onClick={() => navigate("/friends")} className="btn-primary">← Back to Friends</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const profileName = [friendData.first_name, friendData.last_name]
    .filter(Boolean)
    .join(" ")
    .trim() || friendData.display_name || friendData.username || "Runner";

  return (
    <div className="dashboard-new">
      {/* Sidebar */}
      <Sidebar isOpen={sidebarOpen} onToggle={toggleSidebar} />

      {/* Main Container */}
      <div className={`dashboard-main-container ${sidebarOpen ? 'sidebar-open' : 'sidebar-closed'}`}>
        {/* Header */}
        <header className="dashboard-header">
          <div className="header-left">
            <button className="icon-button" onClick={toggleSidebar}>
              <img src={iconMenu} alt="Menu" />
            </button>
            <h1 className="header-title">
              {profileName}
              {friendData.is_current_user && (
                <span style={{
                  background: '#14b8a6',
                  color: 'white',
                  padding: '4px 12px',
                  borderRadius: '12px',
                  fontSize: '14px',
                  fontWeight: '600',
                  marginLeft: '12px'
                }}>
                  This is you!
                </span>
              )}
            </h1>
          </div>
          <div className="header-right">
            <NotificationBell />
            <div className="header-user" onClick={() => navigate('/profile')}>
              <ProfilePicture
                profilePictureUrl={profileData?.profile_picture_url}
                username={profileData?.username}
                size="small"
              />
            </div>
          </div>
        </header>

        {/* Content */}
        <div className="dashboard-content">
          {/* Profile Information */}
          {(friendData.first_name || friendData.last_name || friendData.country) && (
            <div className="chart-card">
              <div className="chart-header">
                <h3>Profile Information</h3>
              </div>
              <div className="chart-content">
                {(friendData.first_name || friendData.last_name) && (
                  <p style={{ margin: '0 0 8px 0' }}>
                    <strong>Name:</strong> {[friendData.first_name, friendData.last_name].filter(Boolean).join(' ')}
                  </p>
                )}
                {friendData.country && (
                  <p style={{ margin: '0' }}>
                    <strong>Country:</strong> {friendData.country}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Stats Cards */}
          <div className="stats-grid">
            <div className="stat-card" style={{ background: 'linear-gradient(135deg, #14b8a6 0%, #0f766e 100%)' }}>
              <div className="stat-value">{friendData.current_streak}</div>
              <div className="stat-label">Current Streak</div>
              <div className="stat-unit">{friendData.current_streak === 1 ? 'day' : 'days'} {friendData.current_streak > 0 && '🔥'}</div>
            </div>

            <div className="stat-card" style={{ background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' }}>
              <div className="stat-value">{friendData.longest_streak}</div>
              <div className="stat-label">Longest Streak</div>
              <div className="stat-unit">{friendData.longest_streak === 1 ? 'day' : 'days'} 🏆</div>
            </div>

            <div className="stat-card" style={{ background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)' }}>
              <div className="stat-value">{friendData.total_run_days}</div>
              <div className="stat-label">Total Run Days</div>
              <div className="stat-unit">days</div>
            </div>

            <div className="stat-card" style={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)' }}>
              <div className="stat-value">{convertMilesToUnit(friendData.total_distance_miles)}</div>
              <div className="stat-label">Total Distance</div>
              <div className="stat-unit">{getUnitLabel()}</div>
            </div>
          </div>

          {/* Personal Card Section */}
          <div className="chart-card">
            <div className="chart-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h3>Personal Note</h3>
                <p>Add notes about yourself or friends</p>
              </div>
              {friendData.is_current_user && (
                <button
                  onClick={() => setIsEditing(!isEditing)}
                  className={isEditing ? 'btn-secondary' : 'btn-primary'}
                >
                  {isEditing ? 'Cancel' : 'Edit Note'}
                </button>
              )}
            </div>
            <div className="chart-content">
              {isEditing ? (
                <div>
                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                      Note Text
                    </label>
                    <textarea
                      value={cardText}
                      onChange={(e) => setCardText(e.target.value)}
                      placeholder="Add a personal note about yourself..."
                      style={{
                        width: '100%',
                        minHeight: '100px',
                        padding: '12px',
                        borderRadius: '6px',
                        border: '1px solid #d1d5db',
                        fontSize: '16px',
                        fontFamily: 'inherit'
                      }}
                    />
                  </div>

                  <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                      Background Color
                    </label>
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                      <input
                        type="color"
                        value={cardColor}
                        onChange={(e) => setCardColor(e.target.value)}
                        style={{ width: '60px', height: '40px', cursor: 'pointer' }}
                      />
                      <input
                        type="text"
                        value={cardColor}
                        onChange={(e) => setCardColor(e.target.value)}
                        placeholder="#ffffff"
                        style={{
                          padding: '8px 12px',
                          borderRadius: '6px',
                          border: '1px solid #d1d5db',
                          width: '120px'
                        }}
                      />
                    </div>
                  </div>

                  <button
                    onClick={savePersonalCard}
                    className="btn-primary"
                  >
                    Save Note
                  </button>
                </div>
              ) : (
                <div
                  style={{
                    background: personalCard?.card_color || '#f9fafb',
                    padding: '20px',
                    borderRadius: '8px',
                    minHeight: '80px',
                    border: '2px solid #e5e7eb'
                  }}
                >
                  {personalCard?.card_text ? (
                    <p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{personalCard.card_text}</p>
                  ) : (
                    <p style={{ color: '#9ca3af', fontStyle: 'italic', margin: 0 }}>
                      {friendData.is_current_user
                        ? 'No note added yet. Click "Edit Note" to add one.'
                        : 'No note available for this friend.'}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FriendProfile;
